import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { isLocale } from '@pickpro/i18n';
import { AppProviders } from './app/providers';
import { installChunkReload } from './app/chunk-reload';
import { routes } from './app/routes';
import { initI18n } from './shared/i18n/i18n';
import { resolveLocale } from './shared/i18n/resolve-locale';
import { readPreferences } from './shared/storage/preferences';
import { initializeTheme } from './shared/storage/theme';
import { publicPagePaths } from './app/public-pages';
import '@fontsource-variable/bricolage-grotesque/opsz.css';
import './styles/index.css';

const start = async (): Promise<void> => {
  installChunkReload();
  initializeTheme();
  let routeLocale: string | undefined;
  try {
    routeLocale = decodeURIComponent(window.location.pathname.split('/')[1] ?? '');
  } catch {
    routeLocale = undefined;
  }
  const locale = isLocale(routeLocale)
    ? routeLocale
    : resolveLocale({
        saved: readPreferences().locale,
        browserLanguages: navigator.languages ?? [],
      });
  document.documentElement.lang = locale;
  await initI18n(locale);
  const router = createBrowserRouter(routes);
  const root = document.getElementById('root')!;
  const isPublicPath =
    isLocale(routeLocale) &&
    publicPagePaths.some(
      (path) =>
        window.location.pathname.replace(/\/$/, '') === `/${routeLocale}${path ? `/${path}` : ''}`,
    );
  const app = (
    <StrictMode>
      <AppProviders>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>
  );
  if (root.hasChildNodes() && isPublicPath) hydrateRoot(root, app);
  else {
    root.replaceChildren();
    createRoot(root).render(app);
  }
};

void start();
